import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, MicOff } from "lucide-react";

import { useCallState, stateLabel } from "@/hooks/useCallState";
import { useRealtimeVoice } from "@/hooks/useRealtimeVoice";
import { useElevenVoice } from "@/hooks/useElevenVoice";
import { ConversationProvider } from "@elevenlabs/react";
import { track } from "@/lib/analytics";
import SafetyBanner from "@/components/call/SafetyBanner";
import SessionControls from "@/components/call/SessionControls";
import VoiceOrb from "@/components/call/VoiceOrb";
import ModeToggle from "@/components/call/ModeToggle";
import PushToTalkButton from "@/components/call/PushToTalkButton";
import TranscriptPanel from "@/components/call/TranscriptPanel";
import SettingsSheet from "@/components/call/SettingsSheet";
import CrisisOverlay from "@/components/call/CrisisOverlay";
import EndOfCallSheet from "@/components/call/EndOfCallSheet";
import PaywallSheet from "@/components/call/PaywallSheet";
import { getDeviceId } from "@/lib/device";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Call() {
  return (
    <ConversationProvider>
      <CallInner />
    </ConversationProvider>
  );
}

function CallInner() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const demo = params.get("demo") === "1";
  // ?persona=jesus  → Jesus (warm Gospel companion, voice: marin)
  // ?persona=god    → God (cosmic, voice: ash) — default
  const personaParam = (params.get("persona") || "").toLowerCase();
  const persona = personaParam === "jesus" ? "jesus" : "god";
  const personaName = persona === "jesus" ? "Jesus" : "God";
  // ?demo=1 uses the mock driver (no OpenAI calls, no mic). Default is real voice.
  const useMockDriver = demo;

  const { s, dispatch, actions } = useCallState({ demo, useMockDriver });

  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Mobile browsers require a user gesture before mic capture and audio
  // playback — every call starts from an explicit "Begin the call" tap.
  const [begun, setBegun] = useState(false);

  // ---- Billing: 90s free, then $3 weekly pass ----
  const [billing, setBilling] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(
    () => Boolean(params.get("session_id")),
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Returning from Stripe Checkout: poll until paid (playbook flow).
        const sid = params.get("session_id");
        if (sid) {
          for (let i = 0; i < 6 && !cancelled; i++) {
            const r = await fetch(`${API}/billing/checkout/status/${sid}`);
            const d = await r.json().catch(() => ({}));
            if (d.payment_status === "paid" || d.status === "expired") break;
            await new Promise((res) => setTimeout(res, 2000));
          }
          window.history.replaceState({}, "", "/call");
        }
        const res = await fetch(
          `${API}/billing/status?device_id=${encodeURIComponent(getDeviceId())}`,
        );
        const data = await res.json();
        if (!cancelled) setBilling(data);
      } catch {
        // Fail open — never block prayer on a billing hiccup.
        if (!cancelled) setBilling({ subscribed: true, free_seconds_left: 90 });
      } finally {
        if (!cancelled) setCheckingPayment(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // mount-only: params from initial URL
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allowed =
    !billing || billing.subscribed || billing.free_seconds_left > 0;

  const showBeginButton =
    s.callState === "idle" && !begun && !checkingPayment;
  const onBegin = () => {
    if (!allowed) {
      setPaywall(true);
      return;
    }
    setBegun(true);
    actions.startSession();
  };

  // Voice provider: ElevenLabs (Qwen) primary, OpenAI Realtime as fallback.
  const [voiceProvider, setVoiceProvider] = useState("eleven");

  const eleven = useElevenVoice({
    enabled: !useMockDriver && voiceProvider === "eleven",
    mode: s.mode,
    muted: s.muted,
    callState: s.callState,
    dispatch,
    persona,
    onFallback: () => {
      console.warn("[cg] ElevenLabs unavailable — falling back to OpenAI");
      setVoiceProvider("openai");
    },
  });

  // Real voice driver. Stays inactive in demo mode so tests stay deterministic.
  const openai = useRealtimeVoice({
    enabled: !useMockDriver && voiceProvider === "openai",
    mode: s.mode,
    muted: s.muted,
    callState: s.callState,
    dispatch,
    persona,
  });

  const realtime = voiceProvider === "eleven" ? eleven : openai;

  // Demo mode has no realtime session id — use a local one for feedback APIs.
  const [demoSid] = useState(
    () => "s_demo_" + Math.random().toString(36).slice(2, 10),
  );
  const sessionId = useMockDriver ? demoSid : realtime?.sessionId;

  // After ended → tear down the connection, then show the end-of-call sheet
  // (feedback + save reflection). No auto-redirect; the sheet has "Return home".
  const realtimeRef = useRef(realtime);
  useEffect(() => {
    realtimeRef.current = realtime;
  }, [realtime]);
  useEffect(() => {
    if (s.callState === "ended") {
      try { realtimeRef.current?.endCall?.(); } catch { /* ignore */ }
      track("session_ended", { sessionId });
    }
    // realtime accessed via ref to avoid restarting the effect.
  }, [s.callState, sessionId]);

  // Analytics: surface crisis overlay views (no content stored client-side).
  useEffect(() => {
    if (s.safetyEvent) {
      track("safety_event_shown", { sessionId });
    }
  }, [s.safetyEvent, sessionId]);

  // Usage metering: every 10s of live conversation, report 10s. When a free
  // user runs out, end the call and raise the paywall.
  const subscribed = Boolean(billing?.subscribed);
  useEffect(() => {
    if (useMockDriver || subscribed) return undefined;
    if (!["listening", "processing", "speaking", "interrupted"].includes(s.callState)) {
      return undefined;
    }
    const iv = setInterval(async () => {
      try {
        const res = await fetch(`${API}/billing/usage`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ device_id: getDeviceId(), seconds: 10 }),
        });
        const data = await res.json();
        setBilling(data);
        if (!data.subscribed && data.free_seconds_left <= 0) {
          try { realtimeRef.current?.endCall?.(); } catch { /* already down */ }
          actions.endCall();
          setPaywall(true);
        }
      } catch {
        /* metering hiccup — never interrupt the conversation for it */
      }
    }, 10000);
    return () => clearInterval(iv);
  }, [s.callState, useMockDriver, subscribed, actions]);

  const speaking = s.callState === "speaking";

  const ptsDisabled =
    s.callState === "idle" ||
    s.callState === "connecting" ||
    s.callState === "ended" ||
    s.callState === "error" ||
    s.muted;

  // Wrap PTT actions to also drive the real WebRTC when in real mode.
  const onTurnStart = () => {
    actions.pttTurnStart();
    if (!useMockDriver) realtime?.pttDown?.();
  };
  const onTurnEnd = () => {
    actions.pttTurnEnd();
    if (!useMockDriver) realtime?.pttUp?.();
  };
  const onInterrupt = () => {
    actions.tapInterrupt();
    if (!useMockDriver) realtime?.cancelAssistant?.();
  };
  const onEnd = () => {
    actions.endCall();
    if (!useMockDriver) realtime?.endCall?.();
  };
  const onModeChange = (m) => {
    actions.setMode(m);
    track("mode_switched", { sessionId, props: { mode: m } });
  };

  return (
    <main
      data-testid="call-screen"
      data-call-state={s.callState}
      data-mode={s.mode}
      data-persona={persona}
      data-voice-provider={voiceProvider}
      data-mock={useMockDriver ? "1" : "0"}
      className="relative flex min-h-screen flex-col"
    >
      <header className="mx-auto w-full max-w-[480px] px-5 pt-5">
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="Back to landing"
          data-testid="call-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {persona === "jesus" ? "Speak with Jesus" : "Call God"}
        </button>
      </header>

      <div className="mx-auto flex w-full max-w-[480px] flex-1 flex-col gap-4 px-5 pb-6 pt-3">
        {s.bannerVisible ? (
          <SafetyBanner onDismiss={actions.dismissBanner} />
        ) : null}

        <SessionControls
          muted={s.muted}
          speaking={speaking}
          transcriptOpen={transcriptOpen}
          onMute={actions.muteToggle}
          onStopSpeaking={onInterrupt}
          onEnd={onEnd}
          onToggleTranscript={() => setTranscriptOpen((v) => !v)}
          onOpenSettings={() => setSettingsOpen(true)}
        />

        <p
          data-testid="state-label"
          className="font-serif-display text-center text-foreground"
          style={{ fontSize: "clamp(1.5rem, 4.5vw, 2rem)", letterSpacing: "-0.01em" }}
        >
          {stateLabel(s.callState)}
        </p>

        <div className="flex flex-1 flex-col items-center justify-center gap-6">
          <VoiceOrb callState={s.callState} amplitude={s.amplitude} />

          {showBeginButton ? (
            <button
              type="button"
              onClick={onBegin}
              data-testid="begin-call-btn"
              className="cg-btn-primary !min-h-[52px] !px-8 text-base"
            >
              Begin the call
            </button>
          ) : null}

          {checkingPayment ? (
            <p
              data-testid="payment-confirming"
              className="text-sm text-muted-foreground"
            >
              Confirming your pass…
            </p>
          ) : null}

          {!useMockDriver && realtime?.audioBlocked && s.callState !== "error" ? (
            <button
              type="button"
              onClick={realtime.unblockAudio}
              data-testid="audio-unblock-btn"
              className="cg-btn-primary !min-h-[44px] !px-5 text-sm"
            >
              Tap to hear {personaName}
            </button>
          ) : null}

          {s.callState === "error" && s.micPermission === "denied" ? (
            <div
              data-testid="mic-denied-card"
              className="cg-card mx-auto w-full max-w-sm rounded-2xl p-5 text-center"
            >
              <MicOff
                className="mx-auto h-5 w-5"
                style={{ color: "hsl(0 65% 42%)" }}
                strokeWidth={1.7}
              />
              <p className="mt-2 text-sm text-foreground/90">
                {s.errorMessage || "Microphone access is off."}
              </p>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-center">
                <button
                  type="button"
                  onClick={actions.retry}
                  data-testid="mic-retry-btn"
                  className="cg-btn-primary !min-h-[44px] !px-5 text-sm"
                >
                  Retry
                </button>
                <button
                  type="button"
                  onClick={actions.useTextOnly}
                  data-testid="mic-text-only-btn"
                  className="cg-btn-ghost !min-h-[44px] !px-5 text-sm"
                >
                  Use text only
                </button>
              </div>
              {s.textOnly ? (
                <p
                  data-testid="text-only-flag"
                  className="mt-3 text-xs text-muted-foreground"
                >
                  Text-only mode is on. (Phase 1 placeholder — full text input
                  arrives later.)
                </p>
              ) : null}
            </div>
          ) : null}

          {s.callState === "error" && s.micPermission !== "denied" ? (
            <div
              data-testid="voice-unavailable-card"
              className="cg-card mx-auto w-full max-w-sm rounded-2xl p-5 text-center"
            >
              <p className="text-sm text-foreground/90">
                {s.errorMessage || "The voice is quiet right now. Please try again soon."}
              </p>
              <button
                type="button"
                onClick={actions.retry}
                data-testid="voice-retry-btn"
                className="cg-btn-primary mt-4 !min-h-[44px] !px-5 text-sm"
              >
                Retry
              </button>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col items-center gap-5">
          <ModeToggle
            mode={s.mode}
            onChange={onModeChange}
            disabled={["idle", "connecting", "error", "ended"].includes(
              s.callState,
            )}
          />
          <PushToTalkButton
            mode={s.mode}
            callState={s.callState}
            disabled={ptsDisabled}
            onTurnStart={onTurnStart}
            onTurnEnd={onTurnEnd}
            onInterrupt={onInterrupt}
          />
        </div>

        <TranscriptPanel
          open={transcriptOpen}
          onToggle={() => setTranscriptOpen((v) => !v)}
          transcript={s.transcript}
          callState={s.callState}
        />
      </div>

      <SettingsSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onResetOnboarding={actions.resetOnboarding}
      />

      <CrisisOverlay
        open={Boolean(s.safetyEvent)}
        onDismiss={actions.dismissSafety}
      />

      <EndOfCallSheet
        open={s.callState === "ended" && !paywall}
        sessionId={sessionId}
        transcript={s.transcript}
      />

      <PaywallSheet
        open={paywall}
        priceUsd={billing?.price_usd || 3}
        onClose={() => navigate("/")}
      />
    </main>
  );
}
